import React from "react";

type Parsed<T> = T extends boolean ? boolean : string;

const bus = new EventTarget();

function parse<T>(raw: string | null, defaultValue?: T): Parsed<T> | null {
    if(raw === null) {
        return (defaultValue ?? null) as Parsed<T> | null;
    }

    if(raw === "true" || raw === "false") {
        return (raw === "true") as Parsed<T>;
    }

    return raw as Parsed<T>;
}

function useLocalStorage<T = string>(key: string, defaultValue?: T): [Parsed<T> | null, (value: Parsed<T>) => void] {
    const [storedValue, setStoredValue] = React.useState<Parsed<T> | null>(() => parse(localStorage.getItem(key), defaultValue));

    React.useEffect(() => {
        const handler = (e: Event) => {
            if((e as CustomEvent).detail === key) {
                setStoredValue(parse(localStorage.getItem(key), defaultValue));
            }
        };

        bus.addEventListener("ls", handler);
        return () => bus.removeEventListener("ls", handler);
    }, [key, defaultValue]);

    const setValue = React.useCallback((value: Parsed<T>) => {
        localStorage.setItem(key, String(value));
        setStoredValue(value);
        bus.dispatchEvent(new CustomEvent("ls", { detail: key }));
    }, [key]);

    return [storedValue, setValue];
}

export default useLocalStorage;
